import React, { useEffect, useState } from 'react';
import { Star, Plus, Trash2, Save, Eye, EyeOff, RefreshCw, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  adminGetReviews, adminUpdateReviewSettings,
  adminCreateReview, adminUpdateReview, adminDeleteReview, adminSyncReviews,
} from '../api/api';

const AVIS_VIDE = { author: '', rating: 5, text: '', date: '', is_visible: true };

export const AdminReviews = () => {
  const [reglages, setReglages] = useState(null);
  const [avis, setAvis] = useState([]);
  const [nouveau, setNouveau] = useState(AVIS_VIDE);
  const [enregistrement, setEnregistrement] = useState(false);
  const [synchro, setSynchro] = useState(false);

  const charger = () =>
    adminGetReviews()
      .then((d) => {
        setReglages(d.settings);
        setAvis(d.items);
      })
      .catch(() => toast.error('Chargement des avis impossible.'));

  useEffect(() => {
    charger();
  }, []);

  const enregistrerReglages = async () => {
    setEnregistrement(true);
    try {
      const maj = await adminUpdateReviewSettings({
        enabled: reglages.enabled,
        rating: reglages.rating ? parseFloat(reglages.rating) : null,
        review_count: reglages.review_count ? parseInt(reglages.review_count, 10) : null,
        profile_url: reglages.profile_url || null,
        review_link: reglages.review_link || null,
        place_id: reglages.place_id || null,
        api_key: reglages.api_key || null,
        auto_sync: reglages.auto_sync,
        min_rating: parseInt(reglages.min_rating, 10),
        max_displayed: parseInt(reglages.max_displayed, 10),
      });
      setReglages(maj);
      toast.success('Réglages enregistrés.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setEnregistrement(false);
    }
  };

  const synchroniser = async () => {
    setSynchro(true);
    try {
      const r = await adminSyncReviews();
      toast.success(r.message);
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Synchronisation impossible.', { duration: 8000 });
    } finally {
      setSynchro(false);
    }
  };

  const ajouter = async () => {
    if (!nouveau.author.trim() || !nouveau.text.trim()) {
      toast.error("L'auteur et le texte sont requis.");
      return;
    }
    try {
      await adminCreateReview({ ...nouveau, order: avis.length });
      setNouveau(AVIS_VIDE);
      toast.success('Avis ajouté.');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ajout impossible.');
    }
  };

  const basculerVisibilite = async (a) => {
    try {
      await adminUpdateReview(a.id, { is_visible: !a.is_visible });
      charger();
    } catch {
      toast.error('Modification impossible.');
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'avis de ${a.author} ?`)) return;
    try {
      await adminDeleteReview(a.id);
      toast.success('Avis supprimé.');
      charger();
    } catch {
      toast.error('Suppression impossible.');
    }
  };

  if (!reglages) return <div className="p-8 text-gray-500">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Avis clients</h1>
        <p className="text-gray-600 mt-1 max-w-2xl">
          Affichez la note de votre fiche Google sur le site, ainsi qu'une sélection d'avis.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-600" aria-hidden="true" />
            Connexion à votre fiche Google
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-gray-600">
            Une fois connectée, la note et les avis se mettent à jour tout seuls, chaque jour.
            Vous n'avez plus rien à recopier.
          </p>

          <div className="space-y-2">
            <Label htmlFor="place">Identifiant de votre fiche (Place ID)</Label>
            <Input
              id="place"
              placeholder="ChIJ…"
              value={reglages.place_id ?? ''}
              onChange={(e) => setReglages((r) => ({ ...r, place_id: e.target.value }))}
            />
            <p className="text-xs text-gray-500">
              Trouvable sur developers.google.com/maps/documentation/places/web-service/place-id
              en cherchant « NETTO+ Liège ».
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cle">Clé API Google Cloud</Label>
            <Input
              id="cle"
              type="password"
              placeholder={reglages.has_api_key ? '•••••••• (clé enregistrée)' : 'AIza…'}
              value={reglages.api_key ?? ''}
              onChange={(e) => setReglages((r) => ({ ...r, api_key: e.target.value }))}
            />
            <p className="text-xs text-gray-500">
              Projet Google Cloud, API « Places API (New) » activée et facturation active.
              Laissez vide pour conserver la clé déjà enregistrée.
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={reglages.auto_sync}
              onChange={(e) => setReglages((r) => ({ ...r, auto_sync: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
            />
            <span className="text-sm text-gray-700">Mise à jour automatique quotidienne</span>
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={synchroniser} disabled={synchro} variant="outline" className="gap-2">
              <RefreshCw className={`w-4 h-4 ${synchro ? 'animate-spin' : ''}`} aria-hidden="true" />
              {synchro ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </Button>
            {reglages.last_sync && (
              <span className="text-sm text-gray-500">
                Dernière mise à jour : {new Date(reglages.last_sync).toLocaleString('fr-BE')}
              </span>
            )}
          </div>

          {reglages.last_sync_error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {reglages.last_sync_error}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Avis affichés sur le site</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minnote">N'afficher que les avis à partir de</Label>
              <select
                id="minnote"
                value={reglages.min_rating}
                onChange={(e) => setReglages((r) => ({ ...r, min_rating: e.target.value }))}
                className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} étoile{n > 1 ? 's' : ''} et plus
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxaff">Nombre maximum affiché</Label>
              <Input
                id="maxaff"
                type="number"
                min="1"
                max="20"
                value={reglages.max_displayed}
                onChange={(e) => setReglages((r) => ({ ...r, max_displayed: e.target.value }))}
              />
            </div>
          </div>

        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Note globale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={reglages.enabled}
              onChange={(e) => setReglages((r) => ({ ...r, enabled: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
            />
            <span className="text-sm font-medium text-gray-900">
              Afficher la section « Avis clients » sur le site
            </span>
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="note">Note globale (remplie par la synchronisation)</Label>
              <Input
                id="note"
                type="number"
                step="0.1"
                min="1"
                max="5"
                placeholder="4.8"
                value={reglages.rating ?? ''}
                onChange={(e) => setReglages((r) => ({ ...r, rating: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nb">Nombre d'avis</Label>
              <Input
                id="nb"
                type="number"
                min="0"
                placeholder="27"
                value={reglages.review_count ?? ''}
                onChange={(e) => setReglages((r) => ({ ...r, review_count: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profil">Lien vers votre fiche Google</Label>
            <Input
              id="profil"
              placeholder="https://maps.app.goo.gl/…"
              value={reglages.profile_url ?? ''}
              onChange={(e) => setReglages((r) => ({ ...r, profile_url: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="laisser">Lien direct pour laisser un avis</Label>
            <Input
              id="laisser"
              placeholder="https://g.page/r/…/review"
              value={reglages.review_link ?? ''}
              onChange={(e) => setReglages((r) => ({ ...r, review_link: e.target.value }))}
            />
            <p className="text-xs text-gray-500">
              Depuis votre fiche Google, bouton « Demander des avis » : Google génère ce lien.
              Envoyez-le à vos clients satisfaits après chaque intervention.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={enregistrerReglages} disabled={enregistrement} className="gap-2">
              <Save className="w-4 h-4" aria-hidden="true" />
              {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Avis affichés ({avis.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {avis.length === 0 && (
            <p className="text-gray-500 text-sm">
              Aucun avis pour l'instant. Recopiez ci-dessous ceux de votre fiche Google.
            </p>
          )}

          {avis.map((a) => (
            <div
              key={a.id}
              className={`border rounded-lg p-4 ${a.is_visible ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{a.author}</span>
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${i <= a.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                          aria-hidden="true"
                        />
                      ))}
                    </span>
                    {a.date && <span className="text-sm text-gray-500">{a.date}</span>}
                  </div>
                  <p className="text-sm text-gray-700 mt-2">{a.text}</p>
                </div>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => basculerVisibilite(a)}
                    className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-50"
                    aria-label={a.is_visible ? 'Masquer' : 'Afficher'}
                  >
                    {a.is_visible
                      ? <Eye className="w-4 h-4" aria-hidden="true" />
                      : <EyeOff className="w-4 h-4" aria-hidden="true" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => supprimer(a)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50"
                    aria-label={`Supprimer l'avis de ${a.author}`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="border-t border-gray-200 pt-5 space-y-4">
            <h4 className="font-semibold text-gray-900">Ajouter un avis</h4>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="auteur">Nom du client</Label>
                <Input
                  id="auteur"
                  value={nouveau.author}
                  onChange={(e) => setNouveau((n) => ({ ...n, author: e.target.value }))}
                  placeholder="Marie D."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="etoiles">Note</Label>
                <select
                  id="etoiles"
                  value={nouveau.rating}
                  onChange={(e) => setNouveau((n) => ({ ...n, rating: parseInt(e.target.value, 10) }))}
                  className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} étoile{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quand">Date</Label>
                <Input
                  id="quand"
                  value={nouveau.date}
                  onChange={(e) => setNouveau((n) => ({ ...n, date: e.target.value }))}
                  placeholder="il y a 2 mois"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="texte">Texte de l'avis</Label>
              <Textarea
                id="texte"
                rows={3}
                value={nouveau.text}
                onChange={(e) => setNouveau((n) => ({ ...n, text: e.target.value }))}
                placeholder="Recopiez l'avis tel qu'il apparaît sur Google"
              />
            </div>

            <Button onClick={ajouter} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" aria-hidden="true" />
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReviews;
